import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * 100% zoom shows this many minutes in the grid's scroller: 6.5 hours, about
 * the prototype's 125% (its 100% is 8 hours).
 */
export const ZOOM_BASE_MINUTES = 6.5 * 60;
/** Px per minute before the scroller is measured (jsdom, first paint). */
export const FALLBACK_PX_PER_MIN = 31 / 15;
const WHEEL_THROTTLE_MS = 90;
const SLOT_MINUTES = 15;

const contentOf = (container: HTMLElement | null) =>
  container?.querySelector<HTMLElement>(".rbc-time-content") ?? null;

interface UseGridZoomArgs {
  zoom: number;
  setZoom: (zoom: number) => void;
  /** Minute of day the grid opens at (mount only). */
  openingMinutes: number;
}

/**
 * Zoom for the Day / Week grid (design handoff "Zoom"):
 *
 * - px/min = (scroller height / 390) × zoom, so 100% shows 6.5 hours. The
 *   scroller is measured on mount and on resize.
 * - One CSS var (`--slot-h`, a 15-min slot's height) on the grid container
 *   rescales everything: RBC positions events in % of the column.
 * - The minute at the top of the scroller is kept across zoom changes
 *   (anchored scroll), and the grid opens at `openingMinutes`.
 * - Ctrl/⌘ + wheel over the grid zooms ±25% (throttled to 90ms).
 *
 * Returns the container's style (the CSS var).
 */
export function useGridZoom(
  containerRef: React.RefObject<HTMLElement | null>,
  { zoom, setZoom, openingMinutes }: UseGridZoomArgs,
): React.CSSProperties {
  const [basePpm, setBasePpm] = useState<number | null>(null);
  const ppm = (basePpm ?? FALLBACK_PX_PER_MIN) * zoom;

  // Measure the scroller (its height doesn't depend on the slot height).
  useLayoutEffect(() => {
    const content = contentOf(containerRef.current);
    if (!content) return undefined;
    const measure = () => {
      const h = content.clientHeight;
      if (h > 0) setBasePpm(h / ZOOM_BASE_MINUTES);
    };
    measure();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    observer?.observe(content);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [containerRef]);

  // The minute at the top of the scroller, tracked on scroll.
  const ppmRef = useRef(ppm);
  ppmRef.current = ppm;
  const topMinRef = useRef<number | null>(null);
  useEffect(() => {
    const content = contentOf(containerRef.current);
    if (!content) return undefined;
    const onScroll = () => {
      topMinRef.current = content.scrollTop / ppmRef.current;
    };
    content.addEventListener("scroll", onScroll, { passive: true });
    return () => content.removeEventListener("scroll", onScroll);
  }, [containerRef]);

  // Re-apply the anchored minute whenever the scale changes (the CSS var is
  // already on the DOM in a layout effect). The first run opens the grid.
  const openingRef = useRef(openingMinutes);
  useLayoutEffect(() => {
    const content = contentOf(containerRef.current);
    if (!content) return;
    if (topMinRef.current === null) topMinRef.current = openingRef.current;
    content.scrollTop = Math.max(0, topMinRef.current * ppm);
  }, [containerRef, ppm]);

  // Ctrl/⌘ + wheel zooms; the listener must be non-passive to stop the
  // browser's own page zoom.
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const setZoomRef = useRef(setZoom);
  setZoomRef.current = setZoom;
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    let last = 0;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const now = Date.now();
      if (now - last < WHEEL_THROTTLE_MS) return;
      last = now;
      setZoomRef.current(zoomRef.current + (e.deltaY < 0 ? 0.25 : -0.25));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [containerRef]);

  return {
    "--slot-h": `${ppm * SLOT_MINUTES}px`,
  } as React.CSSProperties;
}
